import omit from "lodash/omit";
import capitalize from "lodash/capitalize";

import type { Sale } from "src/interfaces/sale.interface";
import type { Inventory } from "src/interfaces/inventory.interface";
import {
  calculateGrossProfit,
  computeMinSaleDate,
  computeMaxSaleDate,
  daysOnPlatform,
} from "src/utils/sales";
import { Allocator } from "../utils";

function fallbackDate(...dates) {
  for (const date of dates) {
    if (date && date.valueOf && date.valueOf() && !isNaN(date.valueOf())) {
      return date;
    }
  }

  return dates[dates.length - 1];
}

export function factorReturnedSale(sale, poshmark): Omit<Sale, "id" | "user"> {
  const ret = {
    ...omit(sale, [
      "id",
      "upload",
      "unreviewed",
      "unmatched",
      "return_id",
      "in_process",
    ]),
    is_return: true,
    sale_price: -(sale.sale_price || 0),
    purchase_price: -(sale.purchase_price || 0),
    shipping_cost: -(sale.shipping_cost || 0),
    extra_shipping_cost: -(sale.extra_shipping_cost || 0),
    extra_transaction_fees: -(sale.extra_transaction_fees || 0),
    shipping_cost_analytics: -(sale.shipping_cost_analytics || 0),
    transaction_fees: -(sale.transaction_fees || 0),
    other_fees: -(sale.other_fees || 0),
    sales_tax: -(sale.sales_tax || 0),
    sale_platform: "Poshmark",
    return_date: fallbackDate(
      new Date(poshmark?.details?.order?.cancelled_on),
      new Date(poshmark?.inventory_booked_at),
      sale.sale_date,
      new Date()
    ),
  } as Omit<Sale, "id" | "user">;

  if (!poshmark?.details?.order?.cancelled_on) ret.return_date_invalid = true;

  if (sale.id) ret.original_id = sale.id;

  ret.gross_profit = calculateGrossProfit(ret);
  ret.min_date = computeMinSaleDate(ret);
  ret.max_date = computeMaxSaleDate(ret);

  return ret;
}

export function mapSale(poshmark): Omit<Sale, "id" | "user">[] {
  const inventoryBookedAt = new Date(poshmark.inventory_booked_at);
  const status = (
    poshmark.display_status ||
    poshmark.details.order.display_status ||
    ""
  )
    .trim()
    .toLowerCase();
  const state = (poshmark.state || poshmark.details?.order?.state || "")
    .trim()
    .toLowerCase();
  const now = new Date();
  const sale = {
    sale_platform: "Poshmark",
    transaction_id: poshmark.id,
    sales_tax: 0,
    shipping_cost: 0,
    transaction_fees: 0,
    sku: "",
    sale_state:
      poshmark.details.order.shipping_method_info?.delivery_info
        ?.shipping_address?.state || "",
    sale_date: fallbackDate(inventoryBookedAt, now),
    purchase_date: fallbackDate(inventoryBookedAt, now),
    list_date: fallbackDate(inventoryBookedAt, now),
    purchase_price: 0,
    sale_price: 0,
    extra_shipping_cost: 0,
    extra_transaction_fees: 0,
    shipping_cost_analytics: 0,
    other_fees: 0,
    liable_to_pay: false,
    ...(state !== "cancelled" && status !== "order complete"
      ? { in_process: true as const }
      : {}),
  };
  // const total = parseFloat(poshmark.total_price_amount?.val) || 0;
  const total =
    poshmark.details.order.line_items.reduce(
      (t, i) => t + parseFloat(i.price_amount?.val) || 0,
      0
    ) || 0;
  const lineItemsLength = poshmark.details.order.line_items.length;
  const discountAllocator = new Allocator(
    parseFloat(poshmark.details.order.seller_discount_amount?.val) || 0,
    total,
    lineItemsLength,
    2
  );
  const salesTaxAllocator = new Allocator(
    parseFloat(poshmark.details.order.total_tax_amount?.val) || 0,
    total,
    lineItemsLength,
    2
  );
  const shippingCostAllocator = new Allocator(
    parseFloat(poshmark.details.order.seller_shipping_discount_amount?.val) ||
      0,
    total,
    lineItemsLength,
    2
  );
  const transactionFeesAllocator = new Allocator(
    (parseFloat(poshmark.details.order.pm_fee_amount?.val) || 0) +
      ((parseFloat(poshmark.details.order.total_earnings_amount?.val) || 0) -
        (parseFloat(poshmark.details.order.seller_earning_amount?.val) || 0)) +
      (parseFloat(poshmark.details.order.commission_tax_amount?.val) || 0),
    total,
    lineItemsLength,
    2
  );
  return poshmark.details.order.line_items
    .filter((l) => !!l)
    .map((lineItem, index) => {
      const price = parseFloat(lineItem.price_amount?.val) || 0;
      const discount = discountAllocator.getAllocation(price);
      const createdAt = new Date(lineItem.product?.data?.created_at);

      const item = {
        ...sale,
        sku: lineItem.sku || sale.sku,
        item_title: lineItem.title,
        sale_price: price - discount,
        sales_tax: salesTaxAllocator.getAllocation(price),
        shipping_cost: shippingCostAllocator.getAllocation(price),
        transaction_fees: transactionFeesAllocator.getAllocation(price),
        category: lineItem.category,
        brand: lineItem.brand,
        sub_category: lineItem.category_features_obj?.[0]?.display || "",
        department: capitalize(
          (lineItem.category_obj?.message_id || "").split("_")[0] || ""
        ),
        purchase_date: fallbackDate(createdAt, sale.purchase_date),
        list_date: fallbackDate(createdAt, sale.list_date),
      } as Omit<Sale, "id" | "user">;

      if (lineItem.product?.data?.first_published_at) {
        const firstPublishedAt = new Date(
          lineItem.product?.data?.first_published_at
        );
        item.purchase_date = fallbackDate(firstPublishedAt, item.purchase_date);
        item.list_date = fallbackDate(firstPublishedAt, item.list_date);
      }

      item.gross_profit = calculateGrossProfit(item);
      const dop = daysOnPlatform(item.sale_date, item.list_date);
      if (dop !== null) item.days_on_platform = dop;
      item.min_date = computeMinSaleDate(item);
      item.max_date = computeMaxSaleDate(item);

      return item;
    });
}

export function mapInventory(
  poshmark,
  { includeZeroQuantity = false } = {}
): Omit<Inventory, "user" | "id">[] {
  const firstPublishedAt = new Date(
    poshmark.first_published_at || poshmark.created_at
  );
  const now = new Date();
  const item = {
    brand: poshmark.brand || "",
    category: poshmark.catalog?.category_obj?.display || "",
    list_date: fallbackDate(firstPublishedAt, now),
    department: poshmark.catalog?.department_obj?.display || "",
    item_title: poshmark.title || "",
    location: "",
    notes: "",
    platforms_listed: ["Poshmark"],
    purchase_date: fallbackDate(firstPublishedAt, now),
    purchase_price: 0,
    sku: "",
    sub_category: poshmark.catalog?.category_feature_objs?.[0]?.display || "",
    // quantity: poshmark.inventory?.size_quantities?.[0].quantity_available || 1,
    quantity: 1,
    listing_id: poshmark.id,
  };

  const items = [] as Omit<Inventory, "user" | "id">[];
  const length = (poshmark.inventory?.size_quantities || []).reduce((a, i) => {
    // Sold-out sizes are dropped by direct import, but pending inventory must
    // keep them so a fully sold-out listing can still be booked.
    if (includeZeroQuantity || i.quantity_available >= 1) return a + 1;
    return a;
  }, 0);

  if (length) {
    for (const size of poshmark.inventory.size_quantities) {
      if (!includeZeroQuantity && size.quantity_available < 1) continue;

      const current = {
        ...item,
        // Preserve a real 0 (sold-out size) for pending inventory; a missing
        // count still falls back to 1.
        quantity:
          size.quantity_available === 0 ? 0 : size.quantity_available || 1,
      } as any;

      if (length > 1 && size.size_obj?.display)
        current.item_title = `${current.item_title} - size ${size.size_obj.display}`;
      if (size.size_id) current.size_id = size.size_id;
      if (size.size_system) current.size_system = size.size_system;

      items.push(current);
    }
  }

  return items;
}
